/**
 * Site content is stored in Firebase (project aimhomoeopathy):
 * - Firestore: one document per data set, ajhm_content/<name>, holding the JSON as text
 * - Storage: uploaded images (AJHM/uploads/images) and PDFs (AJHM/uploads/files)
 * The /admin password is checked in the browser; there are no Firebase user accounts.
 *
 * A data set that has never been saved from the admin panel falls back to public/data/<name>.json,
 * so the site shows its current content until the first save copies it into Firestore.
 */
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebase';

// The aimhomoeopathy project is shared with other AIM apps, so the journal keeps its own collection and folder
const CONTENT_COLLECTION = 'ajhm_content';

const DATA_NAMES = ['journals', 'editors', 'issues', 'pages', 'menu', 'site', 'contact', 'instructions'];

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const friendlyError = (err) => {
  switch (err?.code) {
    case 'permission-denied':
    case 'storage/unauthorized':
      return 'Firebase refused the save: check the Firestore / Storage rules in the Firebase console.';
    case 'storage/unknown':
    case 'storage/bucket-not-found':
    case 'storage/project-not-found':
      return 'Firebase Storage is not set up for this project (Firebase console > Storage > Get started).';
    default:
      return err?.message || 'Something went wrong.';
  }
};

const withFriendlyErrors = async (fn) => {
  try {
    return await fn();
  } catch (err) {
    console.error(err);
    throw new Error(friendlyError(err));
  }
};

// SHA-256 of "<username>:<password>" for the /admin login (the password itself is not in the code).
// To change it: run  echo -n 'admin:NEWPASSWORD' | shasum -a 256  and paste the result here.
const ADMIN_LOGIN_HASH = 'c6d8f95a2474d7cdb7bd8f8a964efbe9f92f7b5a4b01e422ea6f14a12e57bf80';

const sha256 = async (text) => {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(bytes)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

export async function adminLogin(username, password) {
  if ((await sha256(`${username.trim()}:${password}`)) !== ADMIN_LOGIN_HASH) {
    throw new Error('Invalid username or password.');
  }
  return true;
}

export async function adminLogout() {}

/**
 * Reads a data set: Firestore ajhm_content/<name>, else the bundled public/data/<name>.json, else fallback.
 */
export async function loadData(name, fallback) {
  try {
    const snap = await getDoc(doc(db, CONTENT_COLLECTION, name));
    if (snap.exists() && typeof snap.data().json === 'string') {
      return JSON.parse(snap.data().json);
    }
  } catch (err) {
    console.warn(`Could not read ${name} from Firebase, using the bundled copy:`, err);
  }

  try {
    const res = await fetch(`/data/${name}.json`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch (err) {
    console.warn(`Could not load /data/${name}.json:`, err);
    return fallback;
  }
}

/**
 * Saves a data set to Firestore. Live for everyone immediately.
 * name: journals, editors, issues, pages, menu, site, contact or instructions
 */
export async function saveToCode(name, data) {
  if (!DATA_NAMES.includes(name)) throw new Error(`Unknown data set: ${name}`);
  return withFriendlyErrors(async () => {
    // Stored as JSON text: Firestore rejects nested arrays and undefined values
    await setDoc(doc(db, CONTENT_COLLECTION, name), {
      json: JSON.stringify(data),
      updatedAt: serverTimestamp(),
    });
    return true;
  });
}

const safeFileName = (name, allowed) => {
  const lower = String(name || '').toLowerCase();
  const ext = (lower.match(/\.[a-z0-9]+$/) || [''])[0];
  if (!allowed.includes(ext)) throw new Error(`Only ${allowed.join(', ')} files are allowed.`);
  const base = lower.slice(0, -ext.length).replace(/[^a-z0-9_-]/g, '_').replace(/_+/g, '_').slice(0, 80) || 'file';
  return `${base}${ext}`;
};

const upload = (folder, file, allowed) =>
  withFriendlyErrors(async () => {
    if (file.size > MAX_UPLOAD_BYTES) throw new Error('File is larger than 10 MB.');
    const filename = safeFileName(file.name, allowed);
    // Timestamp keeps every upload's URL unique, so an existing link is never overwritten
    const fileRef = ref(storage, `${folder}/${Date.now()}_${filename}`);
    await uploadBytes(fileRef, file, { contentType: file.type || undefined });
    return { url: await getDownloadURL(fileRef), filename };
  });

/**
 * Uploads an image to Firebase Storage. Returns its URL.
 */
export async function uploadImageToCode(file) {
  return (await upload('AJHM/uploads/images', file, ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'])).url;
}

/**
 * Uploads a PDF to Firebase Storage. Returns { url, filename }.
 */
export async function uploadPdfToCode(file) {
  return upload('AJHM/uploads/files', file, ['.pdf']);
}
